'use client'

import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import Storepay from '@/assets/storepay.png'
import Pocket from '@/assets/pocket.png'
import Lend from '@/assets/lendmn.png'
import Omniway from '@/assets/omniway.png'
import Qpay from '@/assets/qpay.svg'
import TransferIcon from '@/assets/transfer.png'
import ProductDescription from '@/components/blocks/ProductDescription'
import StorepayDrawer from './StorepayDrawer'
import PocketDrawer from './PocketDrawer'
import LendDrawer from './LendDrawer'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { ArrowRight, ChevronDown, ChevronRight, Crown, FileDown, FileText, Hammer, Heart, LoaderCircle, Medal, Minus, Percent, Play, Plus, Star } from 'lucide-react'
import { useCart, useWishlist, useVariantSelection, useProductGallery, useProductQuantity, useCartActions } from '@/lib/commerce/hooks'
import {
  formatImageURL,
  formatPrice,
  formatFileSize,
  getEffectivePrice,
  getStrikethroughPrice,
  getDisplayDiscounts,
  isCustomerAuthActive,
  getSuggestedProducts,
  getSalePercent,
  getVariantDimensions,
  getYoutubeVideos,
  getYoutubeVideoId,
  isDiscountBetter,
  getBuyXGetYFallbackTitle,
  getBuyXGetYTagText,
  getLoyaltyProductDiscount,
} from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { trackViewContent } from '@/utils/tracking'
import ProductCard from './ProductCard'
import Review from './Review'
import VariantOptionPicker from '@/components/blocks/VariantOptionPicker'
import { hasOptions } from '@/lib/commerce/variantOptions'

const DISCOUNT_TONE: Record<string, { dot: string, label: string, badgeBg: string, badgeFg: string, badgeBorder: string }> = {
  productDiscount: { dot: '#60a5fa', label: 'Хямдрал', badgeBg: 'rgba(96,165,250,0.16)', badgeFg: '#93c5fd', badgeBorder: 'rgba(96,165,250,0.3)' },
  buyXgetY:        { dot: '#34d399', label: 'Бэлэг',   badgeBg: 'rgba(16,185,129,0.16)', badgeFg: '#6ee7b7', badgeBorder: 'rgba(16,185,129,0.3)' },
  freeShipping:    { dot: '#2dd4bf', label: 'Хүргэлт', badgeBg: 'rgba(45,212,191,0.16)', badgeFg: '#5eead4', badgeBorder: 'rgba(45,212,191,0.3)' },
}

function ActiveDiscounts({ discounts, pathname }: { discounts: any[], pathname: string }) {
  const [open, setOpen] = useState(true)
  if (!discounts || discounts.length === 0) return null

  const maxPct = discounts
    .filter((d: any) => d.type === 'productDiscount' && d.valueType === 'percent')
    .reduce((m: number, d: any) => Math.max(m, d.valuePercent || 0), 0)
  const hasBx = discounts.some((d: any) => d.type === 'buyXgetY')
  const hasFs = discounts.some((d: any) => d.type === 'freeShipping')
  const summary = [
    maxPct > 0 && `−${maxPct}% хүртэл`,
    hasBx && 'Бэлэгтэй',
    hasFs && 'Үнэгүй хүргэлт',
  ].filter(Boolean).join(' · ')

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-7 py-4 text-left cursor-pointer transition-colors"
        style={{ background: 'rgba(16,185,129,0.06)' }}
      >
        <div
          className="h-7 w-7 rounded-md grid place-items-center shrink-0"
          style={{ background: 'rgba(16,185,129,0.16)', border: '1px solid rgba(16,185,129,0.3)', color: '#34d399' }}
        >
          <Percent className="h-3.5 w-3.5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold leading-tight text-white">{discounts.length} урамшуулал идэвхтэй</div>
          {summary && <div className="mt-0.5 text-[11.5px] leading-snug truncate" style={{ color: 'rgba(255,255,255,0.6)' }}>{summary}</div>}
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'rgba(255,255,255,0.5)' }} />
      </button>
      {open && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {discounts.map((d, i) => (
            <div key={d.id || i} style={i < discounts.length - 1 ? { borderBottom: '1px solid rgba(255,255,255,0.07)' } : {}}>
              <DiscountLedgerRow d={d} pathname={pathname} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function DiscountLedgerRow({ d, pathname }: { d: any, pathname: string }) {
  const tone = DISCOUNT_TONE[d.type as keyof typeof DISCOUNT_TONE]
  if (!tone) return null

  if (d.type === 'productDiscount') {
    const badge = d.valueType === 'percent' && d.valuePercent
      ? `-${d.valuePercent}%`
      : d.valueType === 'amount' && d.valueAmount
        ? `-${formatPrice(d.valueAmount)}`
        : null
    return (
      <div className="px-7 py-3.5 flex items-start gap-3">
        <div className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.5)' }}>{tone.label}</span>
            {badge && (
              <span
                className="text-[10.5px] font-bold tabular-nums px-1.5 py-0.5 rounded"
                style={{ background: tone.badgeBg, color: tone.badgeFg, border: `1px solid ${tone.badgeBorder}` }}
              >
                {badge}
              </span>
            )}
          </div>
          <div className="mt-1 text-[13px] font-semibold leading-tight text-white">{d.name}</div>
          <div className="mt-0.5 text-[12px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>Энэхүү урамшуулал бараан дээр идэвхтэй байна.</div>
          {d.haveCampaignPage && d.campaignPageHandle && (
            <a
              href={navigateUrl(`/c/${d.campaignPageHandle}`, pathname)}
              className="inline-flex items-center gap-1 mt-1.5 text-[12px] font-medium transition-colors hover:opacity-80"
              style={{ color: '#93c5fd' }}
            >
              Урамшууллын хуудас үзэх <ArrowRight className="h-3 w-3" strokeWidth={2.25} />
            </a>
          )}
        </div>
      </div>
    )
  }

  if (d.type === 'buyXgetY') {
    const buyX = d.buyXMinQuantity || 1
    const getY = d.getYMaxQuantity || 1
    const reward = d.rewardProducts?.[0] || {}
    const free = reward.valueType === 'free' || !reward.valueType
    const pctVal = reward.valuePercent || d.valuePercent || 0
    const triggers = d.triggerProducts || []
    const isAll = d.buyXMatchMode === 'all' && triggers.length > 1
    const fallbackTitle = getBuyXGetYFallbackTitle(d)
    const tagText = getBuyXGetYTagText(d)
    return (
      <div className="px-7 py-3.5 flex items-start gap-3">
        <div className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.5)' }}>{tone.label}</span>
            <span
              className="text-[10.5px] font-bold tabular-nums px-1.5 py-0.5 rounded"
              style={{ background: tone.badgeBg, color: tone.badgeFg, border: `1px solid ${tone.badgeBorder}` }}
            >
              {tagText}
            </span>
          </div>
          <div className="mt-1 text-[13px] font-semibold leading-tight text-white">{d.name || fallbackTitle}</div>
          <div className="mt-0.5 text-[12px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {isAll ? `Тус бүрээс багадаа ${buyX} ширхэг авбал ` : `${buyX} авбал `}
            {free ? `${getY} бэлэг үнэгүй.` : `${getY} бэлэг ${pctVal}% хямдралтай.`}
          </div>
          {isAll && triggers.length > 0 && (
            <div className="mt-2.5 grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(triggers.length, 3)}, 1fr)` }}>
              {triggers.map((tp: any) => {
                const hasDiscount = tp.priceSale && tp.priceSale > 0 && tp.priceSale < tp.price
                const eff = hasDiscount ? tp.priceSale : tp.price
                return (
                  <a
                    key={tp.id}
                    href={navigateUrl(`/product/${tp.customId || tp.id}`, pathname)}
                    className="flex items-center gap-2 p-2 rounded-md transition-colors hover:opacity-90"
                    style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    {tp.image && <img src={formatImageURL(tp.image)} alt="" className="h-9 w-9 rounded-sm object-cover shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-[11.5px] font-medium leading-tight truncate text-white">{tp.name}</div>
                      {tp.price != null && (
                        <div className="mt-0.5 flex items-baseline gap-1.5">
                          <span className="text-[11px] tabular-nums" style={{ color: hasDiscount ? '#fda4af' : 'rgba(255,255,255,0.8)' }}>{formatPrice(eff)}</span>
                          {hasDiscount && <span className="text-[10px] tabular-nums line-through" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatPrice(tp.price)}</span>}
                        </div>
                      )}
                    </div>
                  </a>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (d.type === 'freeShipping') {
    return (
      <div className="px-7 py-3.5 flex items-start gap-3">
        <div className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.5)' }}>{tone.label}</span>
          <div className="mt-1 text-[13px] font-semibold leading-tight text-white">Үнэгүй хүргэлт</div>
          <div className="mt-0.5 text-[12px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>
            <span className="font-medium text-white">{d.name}</span> — энэхүү бараан дээр үнэгүй хүргэлт идэвхтэй.
          </div>
          {d.isCampaign && d.campaignName && (
            <div className="mt-1 text-[11.5px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{d.campaignName}</div>
          )}
        </div>
      </div>
    )
  }

  return null
}

function FulfillmentBlocks({ product, variant }: { product: any, variant: any }) {
  const items: { key: string, icon: any, title: string, sub: any, color: string, file?: any }[] = []
  if (product?.isDigital) {
    items.push({
      key: 'digital',
      icon: FileDown,
      title: 'Дижитал бүтээгдэхүүн',
      sub: 'Худалдан авсны дараа татаж авна',
      color: '#60a5fa',
      file: variant?.digitalFile || null,
    })
  }
  if (product?.isLimitedEdition) {
    items.push({
      key: 'limited',
      icon: Crown,
      title: 'Limited Edition',
      sub: 'Ховор загварын бүтээгдэхүүн',
      color: '#fb923c',
    })
  }
  if (product?.isOrderBased) {
    items.push({
      key: 'order',
      icon: Hammer,
      title: 'Захиалгаар бэлтгэдэг',
      sub: product?.orderBasedPeriod
        ? <><span className="tabular-nums font-medium text-white">{product.orderBasedPeriod}</span></>
        : 'Тодорхой хугацаанд бэлэн болно',
      color: '#34d399',
    })
  }
  if (items.length === 0) return null

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      {items.map((it, i) => {
        const Icon = it.icon
        return (
          <div
            key={it.key}
            className="px-7 py-4 flex gap-3"
            style={i > 0 ? { borderTop: '1px solid rgba(255,255,255,0.07)' } : {}}
          >
            <div className="w-0.5 rounded-full self-stretch shrink-0" style={{ background: it.color }} />
            <Icon className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.75} style={{ color: it.color }} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold leading-tight text-white">{it.title}</div>
              <p className="mt-0.5 text-[12px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>{it.sub}</p>
              {it.key === 'digital' && it.file && (
                <div
                  className="mt-2.5 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[11.5px]"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <FileText className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} style={{ color: 'rgba(255,255,255,0.5)' }} />
                  <span className="font-medium truncate flex-1 text-white">{it.file.name}</span>
                  <span className="tabular-nums shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }}>{formatFileSize(it.file.size)}</span>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PaymentOptions({ product, vendor, onStorepay, onPocket, onLend }: { product: any, vendor: any, onStorepay: () => void, onPocket: () => void, onLend: () => void }) {
  const isAllowed = (key: string) => product?.paymentOptions?.[key] !== false
  const showQpay = vendor?.qpayMerchant && vendor?.canQpay !== false && isAllowed('qpay')
  const showTransfer = vendor?.canTransfer && vendor?.canSimple !== false && isAllowed('transfer')
  const showStorepay = vendor?.storepayCredentials && vendor?.canStorepay !== false && isAllowed('storepay')
  const showPocket = vendor?.pocketCredentials && vendor?.canPocket !== false && isAllowed('pocket')
  const showLend = vendor?.lendCredentials && vendor?.canLend !== false && isAllowed('lend')
  const showOmniway = vendor?.omniwayCredentials && vendor?.canOmniway !== false && isAllowed('omniway')
  if (!showQpay && !showTransfer && !showStorepay && !showPocket && !showLend && !showOmniway) return null

  return (
    <div className="px-7 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: '#a8a8b3' }}>Төлбөрийн боломжууд</div>
      <div className="space-y-2">
        {showQpay && (
          <PaymentRow icon={<Image src={Qpay} alt="QPay" className="h-7 w-7 object-contain" />} title="QPay" sub="QR код уншуулан төлөх" />
        )}
        {showTransfer && (
          <PaymentRow icon={<Image src={TransferIcon} alt="Transfer" className="h-7 w-7 object-contain" />} title="Дансаар шилжүүлэх" sub="Дэлгүүрийн данс руу шилжүүлэг" />
        )}
        {showStorepay && (
          <PaymentRow onClick={onStorepay} icon={<Image src={Storepay} alt="StorePay" className="h-7 w-7 object-cover rounded" />} title="StorePay" sub="4-6 хуваан төлөх" note="100,000₮+" />
        )}
        {showPocket && (
          <PaymentRow onClick={onPocket} icon={<Image src={Pocket} alt="Pocket" className="h-7 w-7 object-cover rounded" />} title="PocketZero" sub="30-90 хоногт 2-6 хувааж төлөх" note="100,000₮+" />
        )}
        {showLend && (
          <PaymentRow onClick={onLend} icon={<Image src={Lend} alt="Lend" className="h-7 w-7 object-contain" />} title="LendMN" sub="30-60 хоногт 1-4 хувааж төлөх" note="100,000₮+" />
        )}
        {showOmniway && (
          <PaymentRow icon={<Image src={Omniway} alt="OmniWay" className="h-7 w-7 object-cover rounded" />} title="OmniWay" sub="Хуваан төлөх боломжтой" note="100,000₮+" />
        )}
      </div>
    </div>
  )
}

function PaymentRow({ icon, title, sub, note, onClick }: { icon: React.ReactNode, title: string, sub: string, note?: string, onClick?: () => void }) {
  const interactive = !!onClick
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${interactive ? 'cursor-pointer hover:opacity-90' : ''}`}
      style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)' }}
    >
      <div className="shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] font-medium text-white truncate">{title}</span>
          {note && <span className="text-[10px] font-medium tabular-nums shrink-0" style={{ color: 'rgba(255,255,255,0.45)' }}>{note}</span>}
        </div>
        <div className="mt-0.5 text-[11px] truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>{sub}</div>
      </div>
    </div>
  )
}

function Stars({ value, size = 12 }: { value: number, size?: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" style={{ color: '#fcd34d' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          width={size}
          height={size}
          fill={i <= Math.round(value) ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={1.5}
        />
      ))}
    </span>
  )
}

export default function ProductDetail({ productsList, product, vendor, reviews }: { productsList: any[], product: any, vendor: any, reviews: any, wishlist?: any }) {
  const pathname = usePathname()
  const { data: session } = useSession()
  const { loyaltySummary, loyaltyCategoryParents } = useCart()
  const { isInWishlist: isInWishlistFn, toggle: toggleWishlistHook, loading: wishlistLoading } = useWishlist()
  const { addToCartWithRedirect, buyNow, buyNowLoading } = useCartActions(vendor)
  const { selectedVariantId, setSelectedVariantId, selectedVariant, selectedOptions, selectOption, isSelectionPurchasable } = useVariantSelection(product)
  const [storepayOpen, setStorepayOpen] = useState(false)
  const [pocketOpen, setPocketOpen] = useState(false)
  const [lendOpen, setLendOpen] = useState(false)
  const [activeVideo, setActiveVideo] = useState<string | null>(null)

  useEffect(() => {
    if (product?.id) trackViewContent(product)
  }, [product?.id])

  const discounts = getDisplayDiscounts(product, !!session?.user?.id, selectedVariantId ?? undefined)
  const effectivePrice = getEffectivePrice(selectedVariant, discounts)
  const strikethroughPrice = getStrikethroughPrice(selectedVariant, discounts)
  const salePercent = getSalePercent(selectedVariant)
  const showsDiscountBadge = discounts?.length > 0 && isDiscountBetter(selectedVariant, discounts)
  const loyaltyDeal = getLoyaltyProductDiscount({ product, variant: selectedVariant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: showsDiscountBadge })
  const displayEffective = loyaltyDeal ? Math.max(0, (effectivePrice ?? selectedVariant?.price ?? 0) - loyaltyDeal.unitSaving) : effectivePrice
  const displayStrike = loyaltyDeal ? (selectedVariant?.price ?? 0) : strikethroughPrice

  const images = selectedVariant?.images || []
  const placeholder = formatImageURL('placeholder.png')
  const { activeImage, activeImageIndex, selectIndex: handleIndicatorClick, handleTouchStart, handleTouchMove, handleTouchEnd } = useProductGallery(images)
  const { quantity, setQuantity } = useProductQuantity({ variant: selectedVariant, initial: 1 })

  const isInWishlist = isInWishlistFn(product.id, selectedVariantId ?? undefined)
  const wishlistActive = isCustomerAuthActive(vendor)

  const stock = selectedVariant?.stock ?? 0
  const isOut = !isSelectionPurchasable || (!!product?.isTrackable && stock <= 0)
  const quantityNum = typeof quantity === 'number' ? quantity : 1

  const averageRating = reviews?.average || 0
  const totalReviews = reviews?.totalCount || 0

  const suggested = useMemo(() => {
    try {
      return getSuggestedProducts(product, productsList, 4) || []
    } catch {
      return (productsList || []).filter((p: any) => p?.id !== product?.id).slice(0, 4)
    }
  }, [product, productsList])

  const handleAddToCart = async () => {
    if (!selectedVariant) return
    await addToCartWithRedirect({ productId: product.id, quantity: quantityNum, variantId: selectedVariant.id })
  }
  const handleBuyNow = async () => {
    if (!selectedVariant) return
    await buyNow({ productId: product.id, variantId: selectedVariant.id, quantity: quantityNum })
  }
  const handleToggleWishlist = () => {
    toggleWishlistHook({ productId: product.id, variantId: selectedVariant?.id })
  }

  const heroImage = activeImage ? formatImageURL(activeImage) : (images[0] ? formatImageURL(images[0]) : placeholder)
  const variants = product?.variants || []
  const showVariants = variants.length > 1 && !hasOptions(product)
  const brandName = product?.brand?.name
  const categoryName = product?.categories?.[0]?.name

  const videos = useMemo(() => getYoutubeVideos(product).map(url => ({ url, id: getYoutubeVideoId(url) })).filter(v => v.id), [product])
  const activeVideoId = activeVideo ? getYoutubeVideoId(activeVideo) : null
  const selectImage = (i: number) => {
    setActiveVideo(null)
    handleIndicatorClick(i)
  }

  const paymentImage = activeImage ? formatImageURL(activeImage) : (images[0] ? formatImageURL(images[0]) : placeholder)
  const paymentPrice = effectivePrice ?? selectedVariant?.price ?? 0

  return (
    <div>
      <StorepayDrawer isOpen={storepayOpen} onClose={() => setStorepayOpen(false)} vendor={vendor} productPrice={paymentPrice} productName={product?.name} productImage={paymentImage} />
      <PocketDrawer isOpen={pocketOpen} onClose={() => setPocketOpen(false)} vendor={vendor} productPrice={paymentPrice} productName={product?.name} productImage={paymentImage} />
      <LendDrawer isOpen={lendOpen} onClose={() => setLendOpen(false)} vendor={vendor} productPrice={paymentPrice} productName={product?.name} productImage={paymentImage} />
      <div className="cont pt-8 pb-5 text-[12px] flex flex-wrap items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
        <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
        <ChevronRight className="h-3 w-3" style={{ opacity: 0.4 }} />
        <a href={navigateUrl('/product', pathname)} className="hover:text-white transition-colors">Бүтээгдэхүүн</a>
        {categoryName && (
          <>
            <ChevronRight className="h-3 w-3" style={{ opacity: 0.4 }} />
            <a href={navigateUrl(`/product?category=${product.categories[0].id}`, pathname)} className="hover:text-white transition-colors">{categoryName}</a>
          </>
        )}
        <ChevronRight className="h-3 w-3" style={{ opacity: 0.4 }} />
        <span className="text-white truncate">{product?.name}</span>
      </div>

      <div className="cont grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 lg:gap-6 py-4">
        <div>
          <div
            className="relative aspect-square rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
            onTouchStart={activeVideoId ? undefined : handleTouchStart}
            onTouchMove={activeVideoId ? undefined : handleTouchMove}
            onTouchEnd={activeVideoId ? undefined : handleTouchEnd}
          >
            {activeVideoId ? (
              <iframe
                src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1`}
                title={product?.name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
              />
            ) : (
              <img src={heroImage} alt={product?.name} className="w-full h-full object-cover" />
            )}
            {!activeVideoId && ((salePercent && !loyaltyDeal?.saleOffset) || loyaltyDeal) ? (
              <div className="absolute top-5 left-5 flex flex-wrap gap-1.5">
                {salePercent && !loyaltyDeal?.saleOffset ? (
                  <span
                    className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] rounded-md"
                    style={{ background: 'white', color: '#0a0a0c' }}
                  >
                    −{salePercent}%
                  </span>
                ) : null}
                {loyaltyDeal && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] tabular-nums rounded-md bg-violet-600 text-white">
                    <Medal className="h-3 w-3" />
                    -{loyaltyDeal.percent}%
                  </span>
                )}
              </div>
            ) : null}
            {!activeVideoId && wishlistActive && (
              <button
                type="button"
                onClick={handleToggleWishlist}
                disabled={wishlistLoading}
                className="absolute top-5 right-5 w-10 h-10 rounded-full backdrop-blur-xl inline-flex items-center justify-center cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.18)', color: 'white' }}
              >
                {wishlistLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" fill={isInWishlist ? 'white' : 'none'} />}
              </button>
            )}
            {!activeVideoId && images.length > 1 && (
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {images.map((_: any, i: number) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => selectImage(i)}
                    className="h-1 rounded-full transition-all cursor-pointer"
                    style={{ width: i === activeImageIndex ? 28 : 6, background: i === activeImageIndex ? 'white' : 'rgba(255,255,255,0.4)' }}
                    aria-label={`Image ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {(images.length > 1 || videos.length > 0) && (
            <div className="mt-3 grid grid-cols-5 sm:grid-cols-6 gap-2">
              {images.map((src: any, i: number) => {
                const active = !activeVideoId && i === activeImageIndex
                return (
                  <button
                    key={`img-${i}`}
                    type="button"
                    onClick={() => selectImage(i)}
                    className="aspect-square rounded-lg overflow-hidden cursor-pointer transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.035)',
                      border: active ? '2px solid white' : '1px solid rgba(255,255,255,0.13)',
                    }}
                  >
                    <img src={formatImageURL(src)} alt="" className="w-full h-full object-cover" />
                  </button>
                )
              })}
              {videos.map((v, i: number) => {
                const active = activeVideo === v.url
                return (
                  <button
                    key={`vid-${i}`}
                    type="button"
                    onClick={() => setActiveVideo(v.url)}
                    className="aspect-square rounded-lg overflow-hidden cursor-pointer transition-all relative"
                    style={{
                      background: 'rgba(0,0,0,0.5)',
                      border: active ? '2px solid white' : '1px solid rgba(255,255,255,0.13)',
                    }}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${v.id}/mqdefault.jpg`}
                      alt={`Video ${i + 1}`}
                      className="absolute inset-0 w-full h-full object-cover opacity-70"
                    />
                    <div className="absolute inset-0 grid place-items-center">
                      <div
                        className="w-7 h-7 rounded-full grid place-items-center backdrop-blur-md"
                        style={{ background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.3)' }}
                      >
                        <Play className="h-3 w-3 text-white" fill="white" />
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div>
          <div
            className="lg:sticky lg:top-32 self-start rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.13)' }}
          >
            <div className="p-7">
              {brandName && (
                <a
                  href={navigateUrl(`/product?brand=${product.brand.id}`, pathname)}
                  className="text-[10.5px] font-semibold uppercase tracking-[0.22em] hover:opacity-80 transition-opacity"
                  style={{ color: '#a8a8b3' }}
                >
                  {brandName}
                </a>
              )}
              <h1
                className="mt-2 font-semibold tracking-[-0.025em] leading-[1.05] text-white"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(24px, 3vw, 32px)' }}
              >
                {product?.name}
              </h1>
              {totalReviews > 0 && (
                <div className="mt-2 inline-flex items-center gap-2 text-[12px]">
                  <Stars value={averageRating} size={12} />
                  <span className="text-white font-semibold tabular-nums">{averageRating.toFixed(1)}</span>
                  <span style={{ color: 'rgba(255,255,255,0.55)' }}>· {totalReviews}</span>
                </div>
              )}
            </div>

            <div className="px-7 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.25)' }}>
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Үнэ</div>
                  <div className="mt-1.5 flex items-baseline gap-2 flex-wrap">
                    <span
                      className="font-semibold tabular-nums tracking-[-0.02em] text-white leading-none"
                      style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 36 }}
                    >
                      {formatPrice(displayEffective ?? selectedVariant?.price ?? 0)}
                    </span>
                    {displayStrike ? (
                      <span className="text-[12.5px] tabular-nums line-through" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        {formatPrice(displayStrike)}
                      </span>
                    ) : null}
                  </div>
                </div>
                {product?.isTrackable && (
                  <div
                    className="text-right text-[11px] uppercase tracking-[0.16em] shrink-0"
                    style={{ color: isOut ? '#fda4af' : '#34d399' }}
                  >
                    {isOut ? 'Дууссан' : `${stock} үлд.`}
                  </div>
                )}
              </div>
              {loyaltyDeal && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-lg px-3 py-2.5 text-[12px] leading-snug"
                  style={{ background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', color: '#c4b5fd' }}
                >
                  <Medal className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>{loyaltyDeal.tierName} түвшний хөнгөлөлт {loyaltyDeal.percent}%-д хамрагдах бараа тул <span className="font-semibold tabular-nums">{formatPrice(loyaltyDeal.finalPrice)}</span> хөнгөлөлт захиалгад автоматаар тооцогдоно</span>
                </div>
              )}
            </div>

            <ActiveDiscounts
              discounts={[
                ...(isDiscountBetter(selectedVariant, discounts) ? discounts.filter((d: any) => d.type === 'productDiscount') : []),
                ...discounts.filter((d: any) => d.type === 'buyXgetY'),
                ...discounts.filter((d: any) => d.type === 'freeShipping'),
              ]}
              pathname={pathname}
            />

            <FulfillmentBlocks product={product} variant={selectedVariant} />

            {hasOptions(product) && (
              <div className="px-7 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <VariantOptionPicker product={product} selected={selectedOptions} onSelect={selectOption} tone="dark" />
              </div>
            )}

            {showVariants && (
              <div className="px-7 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: '#a8a8b3' }}>Хувилбар</div>
                <div className="space-y-2">
                  {variants.map((v: any) => {
                    const active = v.id === selectedVariantId
                    const unavailable = (v.stock ?? 0) <= 0
                    const vPct = getSalePercent(v)
                    const dims = getVariantDimensions(v)
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => !unavailable && setSelectedVariantId(v.id)}
                        disabled={unavailable}
                        className="w-full text-left p-3 rounded-lg flex items-start justify-between gap-3 transition-colors"
                        style={{
                          background: active ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.25)',
                          border: active ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.13)',
                          opacity: unavailable ? 0.45 : 1,
                          cursor: unavailable ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span
                              className="text-[13px] font-medium text-white truncate"
                              style={unavailable ? { textDecoration: 'line-through' } : {}}
                            >
                              {v.name}
                            </span>
                            {vPct ? (
                              <span
                                className="text-[10px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded shrink-0"
                                style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}
                              >
                                −{vPct}%
                              </span>
                            ) : null}
                          </div>
                          {dims && (
                            <div className="mt-0.5 text-[11px] truncate" style={{ color: 'rgba(255,255,255,0.45)' }}>
                              {dims}
                            </div>
                          )}
                        </div>
                        <span className="text-[12px] tabular-nums shrink-0 pt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          {unavailable ? '—' : formatPrice(v.price)}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="px-7 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center justify-between gap-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Тоо хэмжээ</div>
                <div className="inline-flex items-center rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.13)' }}>
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantityNum - 1))}
                    disabled={isOut || quantityNum <= 1}
                    className="h-11 w-11 inline-flex items-center justify-center text-white cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-80"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="h-11 min-w-12 inline-flex items-center justify-center text-[13.5px] font-semibold text-white tabular-nums px-3">
                    {quantityNum}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.min(product?.isTrackable ? (stock || 999) : 999, quantityNum + 1))}
                    disabled={isOut || (product?.isTrackable && quantityNum >= stock)}
                    className="h-11 w-11 inline-flex items-center justify-center text-white cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-80"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="px-7 pt-5 pb-7 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={isOut}
                className="w-full h-13 rounded-full text-[14px] font-semibold inline-flex items-center justify-center gap-2 cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'white', color: '#0a0a0c', height: 52 }}
              >
                Сагсанд нэмэх — {formatPrice((displayEffective ?? selectedVariant?.price ?? 0) * quantityNum)} <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={isOut || buyNowLoading}
                className="w-full rounded-full text-[13.5px] font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)', height: 48 }}
              >
                {buyNowLoading ? 'Уншиж байна...' : 'Шууд авах'}
              </button>
            </div>

            <PaymentOptions
              product={product}
              vendor={vendor}
              onStorepay={() => setStorepayOpen(true)}
              onPocket={() => setPocketOpen(true)}
              onLend={() => setLendOpen(true)}
            />

            {Array.isArray(product?.specifications) && product.specifications.length > 0 && (
              <div className="px-7 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: '#a8a8b3' }}>Онцлог</div>
                <div className="space-y-2.5">
                  {product.specifications.slice(0, 4).map((f: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-[12.5px] gap-3">
                      <span className="truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>{f.name || f.key}</span>
                      <span className="text-white text-right truncate">{f.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(product?.shortDescription || product?.description) && (
        <div className="cont py-12 max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: '#a8a8b3' }}>Тойм</div>
          {product?.shortDescription && (
            <p className="text-[15px] sm:text-[16px] leading-8 mb-4 text-white" style={{ color: 'rgba(255,255,255,0.9)' }}>
              {product.shortDescription}
            </p>
          )}
          {/* Size/leading and the light-on-dark colours both come from
              `.product-description-invert` — Tailwind utilities are layered and
              would lose to the unlayered `.product-description` block. */}
          {product?.description && (
            <ProductDescription html={product.description} productName={product.name} vendor={vendor} className="product-description product-description-invert" style={{ color: 'rgba(255,255,255,0.78)' }} />
          )}
        </div>
      )}

      {vendor?.canReview && wishlistActive && (
        <Review product={product} vendor={vendor} reviews={reviews} canReview={!!session?.user?.id} />
      )}

      {suggested.length > 0 && (
        <div className="cont pb-20 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-end justify-between gap-4 flex-wrap mb-7">
            <div>
              {brandName && <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: '#a8a8b3' }}>{brandName}</div>}
              <h2 className="text-[22px] sm:text-[26px] font-semibold tracking-[-0.02em] leading-none text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                Танд таалагдаж магадгүй
              </h2>
            </div>
            <a
              href={navigateUrl('/product', pathname)}
              className="text-[13px] font-medium inline-flex items-center gap-1.5 px-4 h-9 rounded-full transition-colors hover:opacity-90"
              style={{ color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.13)' }}
            >
              Бүгдийг харах <ArrowRight className="h-3 w-3" />
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10">
            {suggested.map((p: any) => (
              <ProductCard key={p.id} product={p} vendor={vendor} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
